import React, { useState, useEffect } from 'react';
import { Typography, Paper, Button, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Checkbox, FormControlLabel, TextField, IconButton, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, makeStyles } from '@material-ui/core';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { connect } from 'redux-zero/react';
import actions from '../actions';
import moment from 'moment';
import clsx from 'clsx';
import ConfirmDialog from './ConfirmDialog';

const useStyles = makeStyles(theme => ({
    topBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10
    },
    actionCol: {
        width: 20
    },
    deletedRow: {
        '& td': {
            color: theme.palette.text.disabled
        }
    },
    restoreWarning: {
        marginTop: 10
    },
    billDue: {
        color: theme.palette.error.main
    },
    billDueSoon: {
        color: theme.palette.success.main
    },
    billLink: {
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline'
        }
    },
    search: {
        width: 300,
        maxWidth: '100%',
        marginRight: 20
    }
}));

const mapToProps = ({ selectedSociety }) => ({ selectedSociety });

const billingCycleNames = { Monthly: 'Monthly', Quarterly: 'Quarterly', HalfYearly: 'Half-yearly', Yearly: 'Yearly' };

function SwitchSociety({ selectedSociety, setSelectedSociety, setSelectedSocietyName, setCurrentPeriod }) {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [refreshData, setRefreshData] = React.useState(1);
    const [societyHeaders, setSocietyHeaders] = useState([]);
    const [deleteModel, setDeleteModel] = React.useState();
    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const [search, setSearch] = useState('');
    const [showHidden, setShowHidden] = useState(false);
    const [showDeleted, setShowDeleted] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [copyRow, setCopyRow] = useState(null);
    const [copyName, setCopyName] = useState('');
    const [restoreRow, setRestoreRow] = useState(null);
    const [restoreFile, setRestoreFile] = useState(null);
    const [restoring, setRestoring] = useState(false);
    const [otherSocietyMessage, setOtherSocietyMessage] = useState('');

    function openSociety(row, path) {
        if (row.deleted) return;
        setSelectedSociety(row.id);
        setSelectedSocietyName(row.name);
        setCurrentPeriod({ startDate: row.currentPeriodFrom, endDate: row.currentPeriodTo });
        history.push(path);
    }

    // Red once the next bill is due, green in the week before it
    function nextBillDateClass(row) {
        if (!row.nextBillDate) return undefined;
        const daysLeft = moment(row.nextBillDate).startOf('day').diff(moment().startOf('day'), 'days');
        if (daysLeft <= 0) return classes.billDue;
        if (daysLeft < 7) return classes.billDueSoon;
        return undefined;
    }

    const filteredSocietyHeaders = societyHeaders.filter(x => (showHidden || !x.hidden) && (showDeleted || !x.deleted) && x.name.toLowerCase().includes(search.trim().toLowerCase()));

    useEffect(() => {
        setSelectedSociety(0);
        setSelectedSocietyName('');
        axios.get('/society/list')
            .then(response => {
                setSocietyHeaders(response.data);
            });
    }, [refreshData, setSelectedSociety, setSelectedSocietyName]);

    function ddelete(row) {
        setDeleteModel(row);
        setShowConfirmDelete(true);
    }

    function closeConfirmDialog() {
        setShowConfirmDelete(false);
    }

    function toggleHidden(row) {
        const hidden = !row.hidden;
        axios({
            method: 'PUT',
            url: '/society/' + row.id + '/hidden',
            data: { id: row.id, hidden }
        })
            .then(response => {
                setSocietyHeaders(societyHeaders.map(x => x.id === row.id ? { ...x, hidden } : x));
                enqueueSnackbar(row.name + (hidden ? ' is hidden' : ' is unhidden'), { variant: 'success' });
            });
    }

    function openMenu(e, row) {
        e.stopPropagation();
        setMenuAnchor(e.currentTarget);
        setMenuRow(row);
    }

    function closeMenu() {
        setMenuAnchor(null);
    }

    function openCopyDialog() {
        closeMenu();
        setCopyRow(menuRow);
        setCopyName(menuRow.name + ' (COPY_' + moment().format('DDMMYY_HHmm') + ')');
    }

    function closeCopyDialog() {
        setCopyRow(null);
    }

    function createCopy(e) {
        e.preventDefault();
        const row = copyRow;
        const name = copyName.trim();
        if (!name) return;
        closeCopyDialog();
        enqueueSnackbar('Creating copy of ' + row.name + '...', { variant: 'info' });
        axios({
            method: 'POST',
            url: '/society/' + row.id + '/copy',
            data: { id: row.id, name }
        })
            .then(response => {
                enqueueSnackbar('Created ' + response.data.name, { variant: 'success' });
                setRefreshData(refreshData + 1);
            })
            .catch(error => {
                enqueueSnackbar((error.response && error.response.data && error.response.data.message) || 'Copying ' + row.name + ' failed', { variant: 'error' });
            });
    }

    function restoreSociety() {
        const row = menuRow;
        closeMenu();
        axios({
            method: 'POST',
            url: '/society/' + row.id + '/restore'
        })
            .then(response => {
                setSocietyHeaders(societyHeaders.map(x => x.id === row.id ? { ...x, deleted: false } : x));
                enqueueSnackbar(row.name + ' is restored', { variant: 'success' });
            });
    }

    // Error responses of blob requests are blobs too
    function errorMessage(error, fallback) {
        const data = error.response && error.response.data;
        if (data instanceof Blob) {
            return data.text().then(text => {
                try { return JSON.parse(text).message || fallback; } catch (e) { return fallback; }
            });
        }
        return Promise.resolve((data && data.message) || fallback);
    }

    function backupSociety() {
        const row = menuRow;
        closeMenu();
        enqueueSnackbar('Creating backup of ' + row.name + '...', { variant: 'info' });
        axios.get('/backup/download/' + row.id, { responseType: 'blob' })
            .then(response => {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(response.data);
                link.download = row.name.replace(/[^A-Za-z0-9]+/g, '_') + '_' + moment().format('YYYYMMDD_HHmm') + '.sql.gz';
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(link.href);
            })
            .catch(error => {
                errorMessage(error, 'Backup of ' + row.name + ' failed').then(message => enqueueSnackbar(message, { variant: 'error' }));
            });
    }

    function openRestoreDialog() {
        closeMenu();
        setRestoreRow(menuRow);
        setRestoreFile(null);
        setOtherSocietyMessage('');
    }

    function closeRestoreDialog() {
        if (restoring) return;
        setRestoreRow(null);
    }

    function restoreBackup(allowDifferentSociety) {
        const row = restoreRow;
        const formData = new FormData();
        formData.append('file', restoreFile);
        formData.append('allowDifferentSociety', allowDifferentSociety ? 'true' : 'false');

        setRestoring(true);
        axios({
            method: 'POST',
            url: '/backup/upload/' + row.id,
            headers: { 'Content-Type': 'multipart/form-data' },
            data: formData
        })
            .then(() => {
                setRestoreRow(null);
                enqueueSnackbar(row.name + ' is restored from the backup', { variant: 'success' });
                setRefreshData(refreshData + 1);
            })
            .catch(error => {
                const data = error.response && error.response.data;
                // The backup is of another society, which needs confirming
                if (data && data.target === 'AllowDifferentSociety') {
                    setOtherSocietyMessage(data.message);
                    return;
                }
                errorMessage(error, 'Restore of ' + row.name + ' failed').then(message => enqueueSnackbar(message, { variant: 'error' }));
            })
            .finally(() => setRestoring(false));
    }

    function deleteSociety(row) {
        closeConfirmDialog();
        axios({
            method: 'DELETE',
            url: '/society/' + row.id
        })
            .then(response => {
                setRefreshData(refreshData + 1);
            });
    }

    return (
        <>
            <Typography variant="h5">Select Society</Typography>
            <div className={classes.topBar}>
                <div>
                    <TextField className={classes.search}
                        size="small"
                        label="Search"
                        autoFocus
                        value={search}
                        onChange={(e) => setSearch(e.target.value)} />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />}
                        label="Show hidden" />
                    <FormControlLabel
                        control={<Checkbox size="small" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} />}
                        label="Show deleted" />
                </div>
                {window.decodedJwt.role === 'Administrator' && <Button variant="contained" color="primary" size="small" onClick={() => {
                    setSelectedSociety(0);
                    setSelectedSocietyName('');
                    history.push('/societydetails');
                }}>Create Society</Button>
                }
            </div>
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Billing Cycle</TableCell>
                            <TableCell>Last Bill Date</TableCell>
                            <TableCell>Next Bill Date</TableCell>
                            <TableCell className={classes.actionCol}>Hide</TableCell>
                            <TableCell className={classes.actionCol}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredSocietyHeaders.map(row => (
                            <TableRow hover key={row.id} className={row.deleted ? classes.deletedRow : undefined} onClick={() => openSociety(row, '/transactions')}>
                                <TableCell>{row.name}</TableCell>
                                <TableCell>{billingCycleNames[row.billingCycle] || row.billingCycle}</TableCell>
                                <TableCell>{row.lastBillDate && moment(row.lastBillDate).format('DD/MM/YYYY')}</TableCell>
                                <TableCell className={clsx(nextBillDateClass(row), !row.deleted && classes.billLink)} onClick={(e) => { e.stopPropagation(); openSociety(row, '/bills'); }}>
                                    {row.nextBillDate && moment(row.nextBillDate).format('DD/MM/YYYY')}
                                </TableCell>
                                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                    <Checkbox size="small" checked={!!row.hidden} onChange={() => toggleHidden(row)} />
                                </TableCell>
                                <TableCell padding="checkbox">
                                    <IconButton size="small" aria-label="More actions" onClick={(e) => openMenu(e, row)}>
                                        <MoreVertIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={closeMenu}>
                {menuRow && menuRow.deleted
                    ? <MenuItem onClick={restoreSociety}>Restore</MenuItem>
                    : [
                        <MenuItem key="copy" onClick={openCopyDialog}>Create Copy</MenuItem>,
                        <MenuItem key="backup" onClick={backupSociety}>Backup</MenuItem>,
                        window.decodedJwt.role === 'Administrator' && <MenuItem key="restorebackup" onClick={openRestoreDialog}>Restore Backup...</MenuItem>,
                        <MenuItem key="delete" onClick={() => { closeMenu(); ddelete(menuRow); }}>Delete</MenuItem>
                    ]}
            </Menu>

            <Dialog open={Boolean(copyRow)} onClose={closeCopyDialog} fullWidth maxWidth="sm">
                <form onSubmit={createCopy}>
                    <DialogTitle>Create Copy</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="New Society Name"
                            fullWidth
                            autoFocus
                            value={copyName}
                            error={!copyName.trim()}
                            helperText={!copyName.trim() ? 'Required' : ''}
                            onChange={(e) => setCopyName(e.target.value)} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={closeCopyDialog}>Cancel</Button>
                        <Button type="submit" variant="contained" color="primary" disabled={!copyName.trim()}>Create Copy</Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Dialog open={Boolean(restoreRow)} onClose={closeRestoreDialog} fullWidth maxWidth="sm">
                <DialogTitle>Restore Backup</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        All data of <b>{restoreRow && restoreRow.name}</b> will be replaced with the data in the backup. This can't be undone,
                        so take a backup first if the current data may be needed.
                    </Typography>
                    <input type="file" accept=".gz,.sql" disabled={restoring}
                        onChange={(e) => { setRestoreFile(e.target.files[0] || null); setOtherSocietyMessage(''); }} />
                    {otherSocietyMessage && <Typography variant="body2" color="error" className={classes.restoreWarning}>
                        {otherSocietyMessage} Restore it anyway?
                    </Typography>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeRestoreDialog} disabled={restoring}>Cancel</Button>
                    <Button variant="contained" color="secondary" disabled={!restoreFile || restoring}
                        onClick={() => restoreBackup(Boolean(otherSocietyMessage))}>
                        {restoring ? 'Restoring...' : otherSocietyMessage ? 'Restore Anyway' : 'Restore'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog model={deleteModel}
                show={showConfirmDelete}
                title="Confirm Delete"
                message="Are you sure you want to delete this society? It can be restored later from Show deleted."
                onClose={closeConfirmDialog}
                onYes={deleteSociety} />
        </>
    );
}

export default connect(mapToProps, actions)(SwitchSociety);